import { Page } from "@components-common/layouts";
import { FC, useEffect, useState } from "react";
import { Loader } from "taltech-styleguide";
import { Toolbar } from "@components-common/toolbar";
import { application } from "../../../_runtime/bootstrap";
import { useUserContext } from "../../../_runtime/iam/session.context";
import { ActionButton } from "../../buttons/ActionButton";

const props = {
  title: "EduBIM User Guides",
};

/**
 * Component: UserGuidePage
 *
 * This component represents the main page for managing user guides.
 * It fetches and displays all available user guides and provides options
 * for certain roles to manage the user guides.
 *
 * @component
 * @returns {React.FC} The UserGuidePage component.
 */
export const UserGuidePage: React.FC<any> = () => {
  const [userGuides, setUserGuides] = useState<any>(undefined);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    application
      .getHttpClient()
      .get("/mediasms/mediaitemresources?type=USER_GUIDE")
      .then((res: any) => {
        setUserGuides(res.body!.items);
      });
  }, []);

  useEffect(() => {
    if (userGuides) {
      setLoading(false);
    }
  }, [userGuides]);

  return (
    <Page {...props}>
      {loading ? (
        <Loader overlay />
      ) : (
        <>
          <Toolbar
            left={[
              {
                id: "create_new",
                kind: "create_new",
                roles: ["STUDY_DIRECTOR"],
              },
            ]}
            right={["back"]}
          />
          <h4 className="mb-2">All user guides:</h4>
          <UserGuides
            userGuides={userGuides}
            setUserGuides={setUserGuides}
          />
        </>
      )}
    </Page>
  );
};

/**
 * Component: UserGuides
 *
 * This component is responsible for rendering the list of user guides. It also
 * provides functionality for deleting a user guide if the user has the required role.
 *
 * @component
 * @param {object} props - The props for the component.
 * @param {Array} props.userGuides - An array of user guide objects.
 * @param {function} props.setUserGuides - A function to update the user guides state.
 * @returns {React.FC} The UserGuides component.
 */
const UserGuides: FC<any> = ({ userGuides, setUserGuides }) => {
  const { role } = useUserContext();
  const onDelete = (id: string, index: number) => {
    application
      .getHttpClient()
      .delete(`/mediasms/mediaitemresources/${id}`)
      .then(() => {
        const newUserGuides = userGuides.filter(
          (x: any) => x.id !== id
        );
        setUserGuides(newUserGuides);
      });
  };
  return (
    <div className="d-flex flex-column">
      {userGuides.map((item: any, index: number) => {
        return (
          <div
            key={index}
            className="d-flex align-items-center plugin-version py-2 gap-2"
          >
            <h6 className="mb-0 mt-1">
              <a href={item.url} download={item.url}>
                {item.name.split('.').slice(0, -1).join('.')}
              </a>
            </h6>
            {role === "STUDY_DIRECTOR" && (
              <ActionButton
                kind="delete"
                visual="icon"
                className="ms-3"
                onAction={() => onDelete(item.id, index)}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
